import React, { Component } from "react";
import { Form } from "formsy-react-components";
import AbbreviatedMillionNumberFormat from "../../ui-building-blocks/numbers/AbbreviatedMillionNumberFormat";
import PriceInMillions from "../../ui-building-blocks/numbers/PriceInMillions";
import { getRequest, postRequest, patchRequest } from "../../../api/ApiService";
import SweetAlert from "sweetalert-react";
import EventEmitter from "../../../services/event-emitter";
import { logError } from "../../../services/utils";
import NumberFormat from "react-number-format";
import styled from "styled-components";
import Fragment from "react-dot-fragment";
import ProjectionsTour from "./projections-tour";

const TOUR_STEPS = [
  {
    targetId: null,
    title: "Financial Projections",
    body: (
      <div>
        <p>Welcome! Quick note before we start: these projections are just estimates to help you plan and think through your decisions. Your inputs here will not impact your scores, but working through them sharpens how you think about the business.</p>
      </div>
    ),
  },
  {
    targetId: "ceo-tour-revenues-card",
    title: "Project your revenues",
    body: (
      <div>
        <p>Start here. Project your revenues for the year.</p>
        <ul>
          <li><strong>Units:</strong> how many units you expect to sell.</li>
          <li><strong>Avg. Selling Price:</strong> your total expected sales divided by total units expected to sell. This price can vary if there are multiple products with different pricing and units expected to sell.</li>
        </ul>
        <p>Total Revenues = Units x Avg. Selling Price.</p>
      </div>
    ),
  },
  {
    targetId: "ceo-tour-gross-profits-card",
    title: "Project your cost of sales",
    body: (
      <div>
        <p>Now project what it will cost to produce those units.</p>
        <ul>
          <li><strong>Avg. Unit Costs:</strong> your all-in production costs divided by the total units expected to sell. This cost can vary if there are multiple products with different costs and units expected to sell.</li>
        </ul>
        <p>Total Cost of Sales = Units x Avg. Unit Costs.</p>
        <p>Gross Profit = Total Revenues minus Total Cost of Sales.</p>
      </div>
    ),
  },
  {
    targetId: "ceo-tour-net-profits-card",
    title: "Project your other expenses",
    body: (
      <div>
        <p>Finally, project everything that falls below gross profit. The "Other Expenses" field is a single input that must cover the sum of six expense lines:</p>
        <ul>
          <li><strong>R&amp;D:</strong> product development spend.</li>
          <li><strong>Holding Expenses:</strong> charges for unsold inventory held either at the distribution center or at the production facility.</li>
          <li><strong>Promotion:</strong> promotional spend.</li>
          <li><strong>Selling:</strong> sales team and 3rd party distributor sales commissions.</li>
          <li><strong>Other G&amp;A:</strong> annual depreciation on your facility and any selected distribution centers, plus breakage fees for canceling a distribution center agreement.</li>
          <li><strong>Interest Expense:</strong> interest on outstanding debt.</li>
        </ul>
        <p>Add these together and enter the total. Net Profit = Gross Profit minus Other Expenses minus Taxes.</p>
        <p>Remember, these numbers are your best estimates. You will refine them each round as you learn more about the market. The goal is to strengthen your decision making.</p>
      </div>
    ),
  },
];

const postProjectionsAPI = `${process.env.REACT_APP_API_URL}team/projections/`;

const FONT = "'Mukta Vaani', Lato, sans-serif";
const ACCENT = "#52C5CD";
const ACCENT_DARK = "#3A507B";
const INK = "rgba(0, 27, 59, 0.94)";
const INK_MUTED = "rgba(0, 27, 59, 0.7)";
const INK_SUBTLE = "rgba(0, 27, 59, 0.5)";
const BORDER = "#E5E9F0";

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 24px;
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 4px;
`;

const Title = styled.h1`
  font-family: ${FONT};
  font-weight: 700;
  font-size: 28px;
  line-height: 34px;
  color: ${INK};
  margin: 0;
`;

const Subtitle = styled.p`
  font-family: ${FONT};
  font-weight: 400;
  font-size: 16px;
  line-height: 22px;
  color: ${INK_SUBTLE};
  margin: 0;
  max-width: 640px;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 10px;
  flex-shrink: 0;
`;

const ActionButton = styled.button`
  font-family: ${FONT};
  font-weight: 600;
  font-size: 15px;
  line-height: 20px;
  padding: 10px 22px;
  border-radius: 8px;
  border: 1.5px solid transparent;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease, opacity 0.15s ease;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(ActionButton)`
  background: #fff;
  color: ${INK_MUTED};
  border-color: ${BORDER};

  &:hover:not(:disabled) {
    border-color: rgba(0, 27, 59, 0.3);
    color: ${INK};
  }
`;

const SaveButton = styled(ActionButton)`
  background: ${ACCENT_DARK};
  color: #fff;

  &:hover:not(:disabled) {
    background: #2A4169;
  }
`;

const WalkThroughButton = styled(ActionButton)`
  background: #fff;
  color: ${ACCENT_DARK};
  border-color: ${ACCENT_DARK};
  font-size: 14px;
  padding: 7px 16px;

  &:hover:not(:disabled) {
    background: rgba(58, 80, 123, 0.08);
    color: ${INK};
    border-color: ${INK};
  }
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px;
  align-items: stretch;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid rgba(0, 27, 59, 0.06);
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(0, 27, 59, 0.04);
  padding: 20px 22px;
  display: flex;
  flex-direction: column;
  min-height: 260px;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 12px;
  margin-bottom: 8px;
  border-bottom: 1px solid rgba(0, 27, 59, 0.06);
`;

const CardTitle = styled.h3`
  font-family: ${FONT};
  font-weight: 700;
  font-size: 18px;
  line-height: 24px;
  color: ${INK};
  margin: 0;
`;

const YearBadge = styled.span`
  font-family: ${FONT};
  font-weight: 600;
  font-size: 13px;
  line-height: 18px;
  color: ${ACCENT_DARK};
  background: rgba(58, 80, 123, 0.08);
  padding: 3px 10px;
  border-radius: 12px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0;
  gap: 12px;

  & + & {
    border-top: 1px solid rgba(0, 27, 59, 0.04);
  }
`;

const RowLabel = styled.span`
  font-family: ${FONT};
  font-weight: 500;
  font-size: 15px;
  line-height: 22px;
  color: ${INK_MUTED};
`;

const RowStaticValue = styled.span`
  font-family: ${FONT};
  font-weight: 600;
  font-size: 15px;
  line-height: 22px;
  color: ${INK};
  min-width: 110px;
  text-align: right;
`;

const ModernInput = styled(NumberFormat)`
  border: 1.5px solid ${BORDER};
  border-radius: 8px;
  padding: 7px 12px;
  font-family: ${FONT};
  font-weight: 600;
  font-size: 15px;
  color: ${INK};
  text-align: right;
  outline: none;
  width: 118px;
  background: #fff;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus {
    border-color: ${ACCENT_DARK};
    box-shadow: 0 0 0 3px rgba(58, 80, 123, 0.12);
  }
`;

const TotalPill = styled.div`
  padding: 7px 14px;
  border-radius: 8px;
  font-family: ${FONT};
  font-weight: 700;
  font-size: 15px;
  line-height: 20px;
  min-width: 118px;
  text-align: right;
  background: ${p => (p.subtle ? "rgba(82, 197, 205, 0.18)" : ACCENT)};
  color: ${p => (p.subtle ? INK : "#fff")};
`;

class ProjectionsModal extends Component {
  constructor(props) {
    super(props);

    this.state = {
      year1: props.year1,
      year2: props.year2,
      year3: props.year3,
      loading: false,
      showAlert: false,
      alertText: "",
      currentRound: props.currentRound,
      afterSave: false,
      defaultValues: {
        units_sold: 200000,
        average_selling_price: 5,
        average_unit_costs: 3,
        other_operating_costs: 600000
      }
    };

    this.eventemitter = EventEmitter.getInstance();
  }

  componentWillReceiveProps(nextProps) {
    if (
      (this.state.year1 !== nextProps.year1 ||
        this.state.year2 !== nextProps.year2 ||
        this.state.year3 !== nextProps.year3) &&
      (this.state.currentRound !== nextProps.currentRound ||
        this.state.afterSave === true)
    ) {
      this.setState({
        year1: nextProps.year1,
        currentRound: nextProps.currentRound,
        afterSave: false
      });
    }
  }

  componentDidMount() {
    this.eventemitter.on("ceo-show-modal", this.showModal);
    this.eventemitter.on("ceo-hide-modal", this.hideModal);
    this.eventemitter.on("ceo-force-update", this.rerender);
  }

  componentWillUnmount() {
    this.eventemitter.off("ceo-show-modal", this.showModal);
    this.eventemitter.off("ceo-hide-modal", this.hideModal);
    this.eventemitter.off("ceo-force-update", this.rerender);
  }

  rerender = () => {
    this.forceUpdate();
  };

  get isSaveDisabled() {
    if (this.props.tourMode === false) {
      return false;
    }

    return true;
  }

  hideModal = () => {
    this.props.hideModal();
  };

  showModalTour = () => {
    // do nothing there
  };

  showModal = () => {
    if (this.props.tourMode === true) {
      this.props.showModal();
    } else if (this.props.hasAccess === true) {
      this.props.showModal();
    } else {
      this.props.showRoleAlert();
    }
  };

  hideAlert = () => {
    this.setState({
      showAlert: false
    });
  };

  changeInputElement2 = (e, nonFormattedValue, formatValue, maxValue) => {
    const input = e.target;
    const value =
      (nonFormattedValue instanceof Object && nonFormattedValue.floatValue) ||
      parseFloat(nonFormattedValue);
    const key = input.name;

    this.changeInputElement(key, value, formatValue, maxValue);
    this.changeInputElement(
      key.replace("year1_", "year2_"),
      value,
      formatValue,
      maxValue
    );
    this.changeInputElement(
      key.replace("year1_", "year3_"),
      value,
      formatValue,
      maxValue
    );
  };

  changeInputElement = (key, value, formatValue, maxValue) => {
    let [year, ...param] = key.split("_");
    param = param.join("_");

    if (year in this.state) {
      let data = this.state[year];

      value = parseFloat(value, 10);
      if (isNaN(value)) {
        value = 0;
      }

      if (value < 0) {
        value = 0;
      }

      if (value > maxValue) {
        value = maxValue;
      }

      if (formatValue === true) {
        value *= 1000000;
      }

      data[param] = 0;
      this.setState(
        {
          [year]: data
        },
        () => {
          data[param] = value;

          this.setState({
            [year]: data
          });
        }
      );
    }
  };

  getInputElement = (year_number, key, formatValue, max_value) => {
    let year = year_number - this.props.year_number1 + 1;
    let name = `year${year}_${key}`;
    let value = this.getObjectValue(this.getObject(year), key);

    if (formatValue === true) {
      value /= 1000000;

      return (
        <ModernInput
          thousandSeparator=","
          decimalSeparator="."
          decimalPrecision={2}
          name={name}
          value={value}
          onChange={(e, v) =>
            this.changeInputElement2(e, v, formatValue, max_value)
          }
          suffix=" M"
        />
      );
    }

    return (
      <ModernInput
        prefix="$"
        thousandSeparator=","
        decimalSeparator="."
        decimalPrecision={2}
        name={name}
        value={value}
        onChange={(e, v) =>
          this.changeInputElement2(e, v, formatValue, max_value)
        }
      />
    );
  };

  getRequest = (url, method) => {
    if (method === "patch") {
      return patchRequest(url);
    } else if (method === "get") {
      return getRequest(url);
    }

    return postRequest(url);
  };

  getProjectionsUrl = object_id => {
    let url = postProjectionsAPI;
    let method = "post";
    if (object_id > 0) {
      url += object_id + "/";
      method = "patch";
    }

    return [url, method];
  };

  getProjections = object_id => {
    let [url, method] = this.getProjectionsUrl(object_id);
    return this.getRequest(url, method);
  };

  submitData = () => {
    if (this.props.tourMode === true) {
      // do nothing there
      return;
    }

    this.setState({
      loading: true
    });
    this.updateYear1();
  };

  updateYear1 = () => {
    this.updateYear("year1", this.updateYear2);
  };

  updateYear2 = () => {
    this.updateYear("year2", this.updateYear3);
  };

  updateYear3 = () => {
    this.updateYear("year3", this.closeModal);
  };

  closeModal = () => {
    this.setState(
      {
        loading: false,
        afterSave: true
      },
      () => {
        this.props.onSave();
        this.props.hideModal();
      }
    );
  };

  updateYear = (year, callback) => {
    let data = this.getObject(year);
    let units_sold = this.getObjectValue(data, "units_sold");
    let average_selling_price = this.getObjectValue(
      data,
      "average_selling_price"
    );
    let average_unit_costs = this.getObjectValue(data, "average_unit_costs");

    if (units_sold < 1) {
      this.setState({
        loading: false,
        alertText: "Units cannot be lower then 1",
        showAlert: true
      });
      return;
    }

    if (average_selling_price < 0.1) {
      this.setState({
        loading: false,
        alertText: "Average selling price cannot be lower then 0.1",
        showAlert: true
      });
      return;
    }

    if (average_unit_costs < 0.1) {
      this.setState({
        loading: false,
        alertText: "Average unit costs cannot be lower then 0.1",
        showAlert: true
      });
      return;
    }

    const object_value = this.getObjectValue(data, "id");

    let target_round_number = parseInt(year.replace("year", ""), 10);
    if (isNaN(target_round_number)) {
      target_round_number = 0;
    }

    target_round_number = target_round_number - 1 + this.props.year_number1;

    this.getProjections(object_value)
      .send({
        average_selling_price: average_selling_price,
        average_unit_costs: average_unit_costs,
        capital_expenditures: this.getObjectValue(data, "capital_expenditures"),
        non_operating_expenses: this.getObjectValue(
          data,
          "non_operating_expenses"
        ),
        other_operating_costs: this.getObjectValue(
          data,
          "other_operating_costs"
        ),
        debt_borrowing: this.getObjectValue(data, "debt_borrowing"),
        units_sold: units_sold,
        sales_costs: this.getObjectValue(data, "sales_costs"),
        promo_costs: this.getObjectValue(data, "promo_costs"),
        rnd_costs: this.getObjectValue(data, "rnd_costs"),
        target_round_number: target_round_number
      })
      .then(res => {
        callback();
      })
      .catch(err => {
        if ("detail" in err.response.body) {
          const error = err.response.body["detail"];
          this.setState({
            loading: false,
            alertText: `Error: ${error} Object: ${object_value}`,
            showAlert: true
          });
        }

        logError(err, "ModalCeoProjections", "updateYear");
      });
  };

  /**
   * getUnits
   */
  getObject(year) {
    if (typeof year === "string") {
      year = year.replace("year", "");
      year = parseInt(year, 10);
      if (isNaN(year)) {
        year = 0;
      }
    }

    return this.state[`year${year}`];
  }

  getObjectValue(obj, key) {
    if (key in obj) {
      return obj[key];
    }

    const { currentRound, defaultValues } = this.state;
    if (currentRound === 1 && key in defaultValues) {
      return defaultValues[key];
    }

    return 0;
  }

  getUnits = year => this.getObjectValue(this.getObject(year), "units_sold");

  get unitsYear1() {
    return this.getUnits(1);
  }

  /**
   * getAvgSellingPrice
   */
  getAvgSellingPrice = year =>
    this.getObjectValue(this.getObject(year), "average_selling_price");

  /**
   * getTotalRevenues
   */
  getTotalRevenues = year =>
    this.getUnits(year) * this.getAvgSellingPrice(year);

  get totalRevenuesYear1() {
    return this.getTotalRevenues(1);
  }

  /**
   * getAvgUnitCosts
   */
  getAvgUnitCosts = year =>
    this.getObjectValue(this.getObject(year), "average_unit_costs");

  /**
   * getCostOfSales
   */
  getCostOfSales = year => {
    return this.getUnits(year) * this.getAvgUnitCosts(year);
  };
  get costOfSalesYear1() {
    return this.getCostOfSales(1);
  }

  /**
   * getGrossProfits
   */
  getGrossProfits = year =>
    this.getTotalRevenues(year) - this.getCostOfSales(year);

  get grossProfitsYear1() {
    return this.getGrossProfits(1);
  }

  /**
   * getTotalOperatingExpenses
   */
  getTotalOperatingExpenses = year =>
    this.getObjectValue(this.getObject(year), "other_operating_costs");

  /**
   * getOperatingProfits
   */
  getOperatingProfits = year =>
    this.getGrossProfits(year) - this.getTotalOperatingExpenses(year);

  /**
   * getNonOperatingExpenses
   */
  getNonOperatingExpenses = year =>
    this.getObjectValue(this.getObject(year), "non_operating_expenses");

  /**
   * getIncomeBeforeTaxes
   */
  getIncomeBeforeTaxes = year =>
    this.getOperatingProfits(year) - this.getNonOperatingExpenses(year);

  get incomeBeforeTaxesYear1() {
    return this.getIncomeBeforeTaxes(1);
  }

  /**
   * getTaxes
   */
  getTaxes = year => {
    return this.getIncomeBeforeTaxes(year) < 0
      ? 0
      : this.getIncomeBeforeTaxes(year) * 0.3 * -1;
  };

  /**
   * getNetIncome
   */
  getNetIncome = year =>
    this.getIncomeBeforeTaxes(year) - Math.abs(this.getTaxes(year));

  get netIncomeYear1() {
    return this.getNetIncome(1);
  }

  get inputProps() {
    return {
      type: "text",
      validations: {
        isNumeric: true
      },
      validationErrors: {
        isNumeric: "Only numeric values supported"
      },
      labelClassName: [{ "col-sm-3": false }, "hidden"],
      elementWrapperClassName: [{ "col-sm-9": false }, "custom-error-size"],
      rowClassName: ["form-group", "row", "projections-row"],
      validateOnSubmit: true,
      className: "",
      required: true
    };
  }

  render() {
    const { showModalState, year_number1 } = this.props;

    if (!showModalState) {
      return null;
    }

    return (
      <div>
        <SweetAlert
          show={this.state.showAlert}
          title="Error"
          text={this.state.alertText}
          type="error"
          onConfirm={this.hideAlert}
        />
        <Form onValidSubmit={this.submitData}>
          <HeaderRow>
            <HeaderLeft>
              <TitleRow>
                <Title>CEO Projections</Title>
                <WalkThroughButton
                  type="button"
                  disabled={this.props.showTour === true}
                  onClick={this.props.onStartTour}
                >
                  Walk me through
                </WalkThroughButton>
              </TitleRow>
              <Subtitle>
                Forecast this year's performance so lenders and investors know what to expect.
              </Subtitle>
            </HeaderLeft>
            <HeaderActions>
              {this.state.loading ? (
                <SaveButton type="submit" disabled>Saving...</SaveButton>
              ) : (
                <Fragment>
                  <CancelButton
                    type="button"
                    disabled={this.props.tourMode}
                    onClick={this.hideModal}
                  >
                    Cancel
                  </CancelButton>
                  <SaveButton
                    type="submit"
                    id="ceo-projections-save-button"
                    disabled={this.isSaveDisabled}
                  >
                    Save
                  </SaveButton>
                </Fragment>
              )}
            </HeaderActions>
          </HeaderRow>

          <CardsGrid>
            <Card id="ceo-tour-revenues-card">
              <CardHeader>
                <CardTitle>Revenues</CardTitle>
                <YearBadge>Year {year_number1}</YearBadge>
              </CardHeader>
              <Row>
                <RowLabel>Units</RowLabel>
                {this.getInputElement(year_number1, "units_sold", true, 99)}
              </Row>
              <Row>
                <RowLabel>Avg. Selling Price</RowLabel>
                {this.getInputElement(year_number1, "average_selling_price", false, 100)}
              </Row>
              <Row>
                <RowLabel>Total Revenues</RowLabel>
                <TotalPill>
                  <AbbreviatedMillionNumberFormat
                    value={this.totalRevenuesYear1}
                    prefix="$"
                    decimalPrecision={3}
                    addSpaceBeforeSuffix={true}
                    forceConvert={true}
                  />
                </TotalPill>
              </Row>
            </Card>

            <Card id="ceo-tour-gross-profits-card">
              <CardHeader>
                <CardTitle>Gross Profits</CardTitle>
                <YearBadge>Year {year_number1}</YearBadge>
              </CardHeader>
              <Row>
                <RowLabel>Units</RowLabel>
                <RowStaticValue>
                  <PriceInMillions
                    price={this.unitsYear1}
                    prefix=""
                    baseClassName=""
                    decimalPrecision={3}
                    addSpaceBeforeSuffix={true}
                  />
                </RowStaticValue>
              </Row>
              <Row>
                <RowLabel>Avg. Unit Costs</RowLabel>
                {this.getInputElement(year_number1, "average_unit_costs", false, 100)}
              </Row>
              <Row>
                <RowLabel>Total Cost of Sales</RowLabel>
                <TotalPill subtle>
                  <AbbreviatedMillionNumberFormat
                    value={this.costOfSalesYear1}
                    prefix="$"
                    decimalPrecision={3}
                    addSpaceBeforeSuffix={true}
                    forceConvert={true}
                  />
                </TotalPill>
              </Row>
              <Row>
                <RowLabel>Gross Profits</RowLabel>
                <TotalPill>
                  <AbbreviatedMillionNumberFormat
                    value={this.grossProfitsYear1}
                    prefix="$"
                    decimalPrecision={3}
                    addSpaceBeforeSuffix={true}
                    forceConvert={true}
                  />
                </TotalPill>
              </Row>
            </Card>

            <Card id="ceo-tour-net-profits-card">
              <CardHeader>
                <CardTitle>Net Profits</CardTitle>
                <YearBadge>Year {year_number1}</YearBadge>
              </CardHeader>
              <Row>
                <RowLabel>Other Expenses</RowLabel>
                {this.getInputElement(year_number1, "other_operating_costs", true, 99)}
              </Row>
              <Row>
                <RowLabel>Income Before Taxes</RowLabel>
                <TotalPill subtle>
                  <AbbreviatedMillionNumberFormat
                    value={this.incomeBeforeTaxesYear1}
                    prefix="$"
                    decimalPrecision={3}
                    addSpaceBeforeSuffix={true}
                    forceConvert={true}
                  />
                </TotalPill>
              </Row>
              <Row>
                <RowLabel>Net Profits</RowLabel>
                <TotalPill>
                  <AbbreviatedMillionNumberFormat
                    value={this.netIncomeYear1}
                    prefix="$"
                    decimalPrecision={3}
                    addSpaceBeforeSuffix={true}
                    forceConvert={true}
                  />
                </TotalPill>
              </Row>
            </Card>
          </CardsGrid>
        </Form>
        <ProjectionsTour
          active={this.props.showTour === true}
          steps={TOUR_STEPS}
          onEnd={this.props.onTourEnd}
        />
      </div>
    );
  }
}

export default ProjectionsModal;



// WEBPACK FOOTER //
// ./src/components/ceo/decisions/projections-modal.js